import apiClient from './base';

/**
 * Reports API — generates pour report PDFs (maturity or mass concrete) with
 * options matching the legacy report setup: include graphs, sign report,
 * include attachments, and optionally email the PDF.
 *
 * The backend renders with headless Chromium (Bootstrap + Highcharts),
 * stores the PDF to blob + the report history, and returns it for download.
 */
export type ReportType = 'maturity' | 'mass';

export interface ReportOptions {
  type: ReportType;
  includeGraphs: boolean;
  signature: boolean;
  includeAttachments: boolean;
  email?: string; // if set, the report is emailed to this address
}

export interface ReportHistoryItem {
  report_id: string;
  report_no: string;
  filename: string;
  created_time: string | null;
  created_by: string;
  email: string | null;
}

function triggerDownload(data: Blob, filename: string) {
  const blob = new Blob([data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

async function parseBlobError(error: any): Promise<string> {
  // With responseType 'blob', error bodies come back as a Blob — read it.
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      if (json?.detail) return json.detail;
    } catch {
      /* fall through */
    }
  }
  return error?.message || 'Failed to generate report';
}

export const reportsAPI = {
  /**
   * Generate a pour report. If `email` is set the report is emailed (and not
   * downloaded); otherwise it downloads in the browser. Returns whether it
   * was emailed.
   */
  generateReport: async (
    pourId: string,
    opts: ReportOptions,
  ): Promise<{ emailed: boolean }> => {
    try {
      const response = await apiClient.get(`/api/reports/pour/${pourId}`, {
        params: {
          type: opts.type,
          include_graphs: opts.includeGraphs,
          signature: opts.signature,
          include_attachments: opts.includeAttachments,
          email: opts.email || '',
        },
        responseType: 'blob',
      });

      const emailed = (response.headers?.['x-report-emailed'] as string) === 'true';
      if (!emailed) {
        const disposition = (response.headers?.['content-disposition'] as string) || '';
        const match = disposition.match(/filename="([^"]+)"/);
        const filename = match ? match[1] : `${pourId}-${opts.type}-report.pdf`;
        triggerDownload(response.data, filename);
      }
      return { emailed };
    } catch (error: any) {
      throw new Error(await parseBlobError(error));
    }
  },

  /** List previously generated reports for a pour. */
  getHistory: async (pourId: string): Promise<ReportHistoryItem[]> => {
    const response = await apiClient.get(`/api/reports/pour/${pourId}/history`);
    return response.data;
  },

  /** Download a previously generated report from history. */
  downloadFromHistory: async (reportId: string, filename: string): Promise<void> => {
    const response = await apiClient.get(`/api/reports/${reportId}/download`, {
      responseType: 'blob',
    });
    triggerDownload(response.data, filename);
  },
};

export default reportsAPI;
